// This phase class is how we define the phase data as a user
// It's formatted in a way that's convenient for thinking about it that way
export class PhaseInput {
    words: string[];
    ingredients: string[];
    altSolutions: string[];

    constructor(words: string[], ingredients: string, altSolutions?: string[]) {
        this.words = words;
        this.ingredients = [...ingredients.toLowerCase()];
        this.altSolutions = [];
        if (altSolutions && altSolutions.length > 0) {
            for (const altSolution of altSolutions) {
                // checks for duplicates in case user error:
                if (!this.altSolutions.includes(altSolution)) {
                    this.altSolutions.push(altSolution.toLowerCase());
                }
            }
        }
    }
}

// Phase is a TYPE to allow easy JSON serialization in snapshot
export type Phase = {
    altSolutions: string[],
    wordIndices: number[],
    ingredients: string[],
    index: number
}

export class StoryLevelData {
    levelNumber: number = -1; // set to negative to prevent serving if not properly initialized in FullStory
    levelName: string;
    levelID: string;
    words: string[];
    phases: Phase[];

    constructor(name: string, id: string, phaseInputs: PhaseInput[]) {
        this.levelName = name;
        this.levelID = id;
        // initialize the words array and phase array
        this.words = [];
        this.phases = [];
        // iterate through the phase inputs
        for (const [index, phaseInput] of phaseInputs.entries()) {
            const phase = this.buildPhase(phaseInput, index);
            // add it to the array
            this.phases.push(phase);
        }
    }

    // Converts a phase input to a phase used as data for the puzzle
    buildPhase(phaseInput: PhaseInput, phaseIndex: number) : Phase {
        const phase: Phase = StoryLevelData.createDummyPhase();
        // we want to add each word to the wordModule array
        // and register its index in the phase
        for (const word of phaseInput.words) {
            this.words.push(word);
            const wordIndex = this.words.length - 1;
            phase.wordIndices.push(wordIndex);
        }
        // set the other fields for the phase
        phase.ingredients = phaseInput.ingredients;
        phase.altSolutions = phaseInput.altSolutions;
        phase.index = phaseIndex;
        return phase;
    }

    static createDummyPhase() : Phase {
        return {
            altSolutions: [],
            wordIndices: [],
            ingredients: [],
            index: -1 // dummy value until we initialize
        }
    }

    // note the return type, it's a "type predicate"
    // If the function returns true, it treats x as that type
    // NOTE THIS TREATS OBJECTS AS INSTANCES, IT DOES NOT CARE ABOUT METHODS
    static isStoryLevelData(x: unknown) : x is StoryLevelData {
        if (typeof x !== 'object' || x === null)
            return false;
        
        const obj = x as any;
        // check primitives
        if (typeof obj.levelNumber !== 'number')
            return false;
        if  (typeof obj.levelName !== 'string')
            return false;
        if (typeof obj.levelID !== 'string')
            return false;

        // words: string[]
        if (!Array.isArray(obj.words))
            return false;
        if (!obj.words.every((word: any) => typeof word === 'string'))
            return false;

        // phases: Phase[]
        if (!Array.isArray(obj.phases))
            return false;
        if (!obj.phases.every(this.isPhase))
            return false;

        return true;
    }

    static isPhase(x: unknown) : x is Phase {
        if (typeof x !== 'object' || x === null) {
            console.log('isPhase exit 1')
            return false;
        }

        const obj = x as any;
        // solutions: string[]
        if (!Array.isArray(obj.altSolutions)) {
            console.log('isPhase exit 2')
            return false;
        }
        if (
            obj.altSolutions.length > 0
            && !obj.altSolutions.every((word: any) => typeof word === 'string')
        ) {
            console.log('isPhase exit 3')
            return false;
        }

        // wordIndices: number[]
        if (!Array.isArray(obj.wordIndices)) {
            console.log('isPhase exit 4')
            return false;
        }
        if (!obj.wordIndices.every((index: any) => typeof index === 'number')) {
            console.log('isPhase exit 5')
            return false;
        }
        
        // ingredients: string[]
        if (!Array.isArray(obj.ingredients)) {
            console.log('isPhase exit 6')
            return false;
        }
        if (!obj.ingredients.every((char: any) => typeof char === 'string' && char.length === 1)) {
            console.log('isPhase exit 7')
            return false;
        }
        
        // index: number
        if (typeof obj.index !== 'number') {
            console.log('isPhase exit 8')
            return false;
        }

        return true;
    }
}